import { invoiceObjectDetails, itemObjectDetails, vendorObjectDetails } from "@finway-group/shared/lib/consts"
import {
    DocumentTypeEnum,
    ExpenseStatusEnum,
    OcrInvoiceData,
    OcrItem,
    OcrMetadataInterface,
    OcrResult,
    OcrVendor,
    Vendor,
    Expense,
    InvoiceInterface,
} from "@finway-group/shared/lib/models"
import { UploadKindEnum } from "@finway-group/shared/lib/models"
import { Card, Form, Spin, Switch, Tabs } from "antd"
import { FormInstance } from "antd/lib/form"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import DocumentTabManager from "Components/DocumentTabManager"
import { ExpenseRules } from "Components/forms/rules"
import { FileService, NotificationService, OcrService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { checkFileSize, mapToInvoiceInterface } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { fillFormWithOcrData } from "Shared/utils/ocr.utils"
import { useTaxes } from "Shared/hooks/tax.hooks"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { useLoggedInUserProfile } from "Shared/hooks/user.hooks"
import { checkForDuplicateInvoiceFile, isExpenseAlreadyPaid, isHospitalityExpense } from "Features/pages/expenses/expenseUtils"

interface CommonDocumentUploadInterface {
    expenseForm: FormInstance
    isSubscription: boolean
    ocrSettings?: {
        isOcrEnabled: boolean
        isOcrItemizationEnabled: boolean
        setIsOcrEnabled: (flag: boolean) => void
        setIsOcrItemizationEnabled: (flag: boolean) => void
    }
    expenseInvoices: InvoiceInterface[]
    expenseDeliveryNote?: string
    isUploading: boolean
    setIsUploading: (flag: boolean) => void
    expense: Expense
    updateExpense: (expense: any) => void
    vendors: Vendor[]
    inboxInvoices?: InvoiceInterface[]
    invoicesRequired?: boolean
    isNew?: boolean
    expenseOffers?: string[]
    setIsOcrProcessed: (flag: boolean) => void
    activeTab: string
    onTabClick(key: string, _event: React.KeyboardEvent | React.MouseEvent): any
    setPrefilledVendorData: (vendorData: any) => void
}

const CommonDocumentUpload: React.FC<CommonDocumentUploadInterface> = ({
    expenseForm,
    isSubscription,
    expenseInvoices,
    expenseDeliveryNote,
    ocrSettings,
    isUploading,
    setIsUploading,
    expense,
    updateExpense,
    vendors,
    inboxInvoices,
    invoicesRequired = false,
    isNew,
    expenseOffers,
    setIsOcrProcessed,
    activeTab,
    onTabClick,
    setPrefilledVendorData,
}) => {
    const { t } = useTranslation()
    const [invoices, setInvoices] = useStateIfMounted<InvoiceInterface[]>(inboxInvoices ?? expenseInvoices)
    const [deliveryNote, setDeliveryNote] = useStateIfMounted(expenseDeliveryNote)
    const [isOcrUploading, setIsOcrUploading] = useStateIfMounted(false)
    const { invoiceUpload } = ExpenseRules(undefined, undefined, invoices)
    const [activeInvoiceTabKey, setActiveInvoiceTabKey] = useStateIfMounted(0)
    const [invoicesOcrProcessed, setInvoicesOcrProcessed] = useStateIfMounted(inboxInvoices ?? expenseInvoices)
    const [fileObject, setFileObject] = useStateIfMounted<any>(undefined)
    const [offers, setOffers] = useStateIfMounted([...(expenseOffers || [])])
    const taxes = useTaxes()
    const loggedInUser = useLoggedInUserProfile()
    const isAlreadyPaid = isExpenseAlreadyPaid(expense)
    const [showStampOnInvoice, setShowStampOnInvoice] = useStateIfMounted(true)

    useEffect(() => {
        let mounted = true
        const currentInvoice: any = invoices[activeInvoiceTabKey]
        if (ocrSettings?.isOcrEnabled && isNotOcrProcessed(currentInvoice)) {
            // perform the OCR
            setIsOcrUploading(true)
            OcrService.uploadAndRetrieve(currentInvoice?.url)
                .then(({ ocrObject, plottedPDF }) => {
                    if (mounted) {
                        fillFormWithOcrData(
                            expenseForm,
                            expense,
                            ocrObject,
                            vendors,
                            taxes,
                            updateExpense,
                            setPrefilledVendorData,
                            loggedInUser.settings.globalCurrency,
                            ocrSettings.isOcrItemizationEnabled,
                            t
                        )

                        // TODO: display the plotted version for now
                        // plottedPDF && displayPlottedPDF(plottedPDF, ocrObject);
                        setInvoicesOcrProcessed([...invoicesOcrProcessed, currentInvoice])
                        setIsOcrProcessed(true)
                        NotificationService.send(NotificationTypeEnum.INFO, t("notification:ocr.processed.title"), t("notification:ocr.processed.message"))
                    }
                })
                .catch(() => NotificationService.send(NotificationTypeEnum.WARNING, t("error:ocr.title"), t("error:ocr.message")))
                .finally(() => setIsOcrUploading(false))
        }
        return () => {
            mounted = false
        }
    }, [invoices, ocrSettings?.isOcrEnabled])

    const isNotOcrProcessed = (invoice: InvoiceInterface) => invoice && invoicesOcrProcessed.indexOf(invoice) === -1

    const displayPlottedPDF = (plottedPDF: Buffer, ocrObject: OcrResult) => {
        setFileObject({
            activeKey: activeInvoiceTabKey,
            fileData: Uint8Array.from(Object.values(plottedPDF)),
            legendContent: filterLegend(ocrObject),
        })
    }

    const filterLegend = (ocrObject: OcrResult) => {
        const legendContent: OcrMetadataInterface[] = []
        Object.keys(invoiceObjectDetails).forEach((key: keyof OcrInvoiceData) => {
            if (ocrObject.invoiceData[key].boundingBox) {
                legendContent.push(invoiceObjectDetails[key])
            }
        })
        Object.keys(vendorObjectDetails).forEach((key: keyof OcrVendor) => {
            if (ocrObject.vendor[key].boundingBox) {
                legendContent.push(vendorObjectDetails[key])
            }
        })
        Object.keys(itemObjectDetails).forEach((key: keyof OcrItem) => {
            if (ocrObject.items.some((item: OcrItem) => item[key].boundingBox)) {
                legendContent.push(itemObjectDetails[key])
            }
        })
        return legendContent
    }

    const doFileUpload = async (file: any, kind: DocumentTypeEnum) => {
        if (!checkFileSize(file)) return

        setIsUploading(true)
        try {
            const data = await FileService.upload(file, UploadKindEnum.EXPENSE.toString())
            switch (kind) {
                case DocumentTypeEnum.INVOICE:
                    if (await checkForDuplicateInvoiceFile(data.duplicateData)) {
                        const formInvoices = expenseForm.getFieldValue("invoices")
                        const newFileData = {
                            fileName: file.name,
                            url: data.url,
                            hash: data.hash,
                            uploadDate: new Date(),
                        }
                        setInvoices([...formInvoices, newFileData])
                        updateExpense({ invoices: [...formInvoices, newFileData] })
                    }
                    break
                case DocumentTypeEnum.DELIVERY_NOTE:
                    setDeliveryNote(data.url)
                    updateExpense({ deliveryNote: data.url })
                    break
                case DocumentTypeEnum.OFFER:
                    const formOffers = expenseForm.getFieldValue("offers")
                    setOffers([...formOffers, data.url])
                    updateExpense({ offers: [...formOffers, data.url] })
                    break
                default:
                    break
            }
        } catch (err) {
            NotificationService.send(NotificationTypeEnum.ERROR, t("error:file.upload.title"), t("error:file.upload.message"))
        } finally {
            setIsUploading(false)
        }
    }

    const onRemoveTab = (documentType: DocumentTypeEnum, targetKey: string | React.MouseEvent | React.KeyboardEvent) => {
        switch (documentType) {
            case DocumentTypeEnum.INVOICE:
                let invoicesCopy = [...invoices]
                invoicesCopy = invoicesCopy.filter((_, i) => i !== Number(targetKey))
                setInvoices(invoicesCopy)
                const formInvoices = expenseForm.getFieldValue("invoices") as any[]
                updateExpense({ invoices: formInvoices.filter((_, i) => i !== Number(targetKey)) })
                break
            case DocumentTypeEnum.DELIVERY_NOTE:
                setDeliveryNote(undefined)
                updateExpense({ deliveryNote: undefined })
                break
            case DocumentTypeEnum.OFFER:
                let offerCopy = [...offers]
                offerCopy = offerCopy.filter((_, i) => i !== Number(targetKey))
                setOffers(offerCopy)
                updateExpense({ offers: offerCopy })
                break
            default:
                break
        }
    }

    const onChangeInvoicesActiveKey = (activeInvoiceTabKey: number) => {
        setActiveInvoiceTabKey(Math.max(activeInvoiceTabKey, 0))
    }

    const toggles = (
        <div className="mr-10 ml-10 text-sm ">
            {isOcrUploading && <Spin className="mr-10" />}
            <span className="mr-10">{t("input:settings.enable_ocr")}</span>
            <Switch size="small" defaultChecked={ocrSettings?.isOcrEnabled} onChange={(checked) => ocrSettings?.setIsOcrEnabled(checked)} />
            <span className="mr-10 ml-10">{t("input:settings.inline_items")}</span>
            <Switch
                disabled={!ocrSettings?.isOcrEnabled || isHospitalityExpense(expense)}
                size="small"
                defaultChecked={ocrSettings?.isOcrItemizationEnabled}
                onChange={(checked) => ocrSettings?.setIsOcrItemizationEnabled(checked)}
            />
            {isAlreadyPaid && (
                <>
                    <span className="mr-10 ml-10">{t("input:settings.show_stamp")}</span>
                    <Switch size="small" defaultChecked={showStampOnInvoice} onChange={(checked) => setShowStampOnInvoice(checked)} />
                </>
            )}
        </div>
    )

    return (
        <>
            <Card className="w-full card-with-tabs h-full">
                <Tabs
                    activeKey={activeTab}
                    onTabClick={onTabClick}
                    className="w-full min-h-200 ml-1"
                    tabBarExtraContent={ocrSettings && toggles}
                    defaultActiveKey={DocumentTypeEnum.INVOICE}
                >
                    <Tabs.TabPane key={DocumentTypeEnum.OFFER} tab={t("input:request.offer")}>
                        <Form.Item name="offers" key="offers">
                            <DocumentTabManager
                                invoices={mapToInvoiceInterface(offers)}
                                fileObject={fileObject}
                                doFileUpload={doFileUpload}
                                isUploading={isUploading}
                                onRemoveTab={onRemoveTab}
                                documentType={DocumentTypeEnum.OFFER}
                                label={{
                                    buttonLabel: t("action:add_offer"),
                                    draggerDescription: t("action:or_drop_files"),
                                }}
                            />
                        </Form.Item>
                    </Tabs.TabPane>
                    <Tabs.TabPane key={DocumentTypeEnum.INVOICE} tab={<span className={invoicesRequired ? `label-required` : ""}>{t("input:request.invoice")}</span>}>
                        <Form.Item name="invoices" key="invoices" rules={invoicesRequired && isNew ? invoiceUpload : []}>
                            <DocumentTabManager
                                invoices={invoices}
                                fileObject={fileObject}
                                deliveryNote={deliveryNote}
                                doFileUpload={doFileUpload}
                                isUploading={isUploading}
                                onRemoveTab={onRemoveTab}
                                documentType={DocumentTypeEnum.INVOICE}
                                label={{
                                    buttonLabel: t("action:add_invoice"),
                                    draggerDescription: t("action:or_drop_files"),
                                }}
                                onChangeInvoicesActiveKey={onChangeInvoicesActiveKey}
                                readOnly={expense.deleted || (expense.status === ExpenseStatusEnum.DECLINED && expense.paymentOption === ExpensePaymentOptionEnum.SMART_CARD)}
                                showPaidStamp={isAlreadyPaid && showStampOnInvoice}
                            />
                        </Form.Item>
                    </Tabs.TabPane>

                    {!isSubscription && (
                        <Tabs.TabPane key={DocumentTypeEnum.DELIVERY_NOTE} tab={t("input:request.delivery_note")}>
                            <Form.Item name="deliveryNote" key="deliveryNote">
                                <DocumentTabManager
                                    invoices={invoices}
                                    deliveryNote={deliveryNote}
                                    doFileUpload={doFileUpload}
                                    onRemoveTab={onRemoveTab}
                                    documentType={DocumentTypeEnum.DELIVERY_NOTE}
                                    label={{
                                        buttonLabel: t("action:add_delivery_note"),
                                        draggerDescription: t("action:or_drop_files"),
                                    }}
                                />
                            </Form.Item>
                        </Tabs.TabPane>
                    )}
                </Tabs>
            </Card>
        </>
    )
}

export default CommonDocumentUpload
